import React, { useMemo } from 'react'
import { Flag } from 'react-bootstrap-icons'
import { MapContainer, Popup, TileLayer, GeoJSON, Circle } from 'react-leaflet'
import EChartsReact from 'echarts-for-react';
import { observer } from 'mobx-react-lite';
import getUnicodeFlagIcon from 'country-flag-icons/unicode'
import Switch from '../../../../../FormComponents/Switch'

export default observer(function Countries({ authentications, values, setFieldValue, advSideScenario }) {

  const country = useMemo(() => {
    if (authentications.length === 0) return null;
    
    // Create a map to combine country data by country_code
    const countryMap = new Map();
    
    authentications.forEach(auth => {
      const countryData = auth?.audience_information?.detailed_audience_stats?.country;
      if (!countryData) return;
      
      countryData.forEach(item => {
        if (!item || !item.country_code) return;
        
        const existing = countryMap.get(item.country_code);
        if (existing) {
          existing.count++;
          existing.percentage += item.percentage || 0;
          // Keep polygon data if it exists
          if (!existing.polygon && item.polygon) {
            existing.polygon = item.polygon;
          }
        } else {
          countryMap.set(item.country_code, {
            country_code: item.country_code,
            name: item.name,
            polygon: item.polygon || null,
            percentage: item.percentage || 0,
            count: 1
          });
        }
      });
    });
    
    // Calculate average percentages and convert to array
    return Array.from(countryMap.values())
      .map(item => ({
        country_code: item.country_code,
        name: item.name,
        polygon: item.polygon,
        percentage: item.percentage / item.count
      }))
      .sort((a, b) => b.percentage - a.percentage);
  }, [authentications]);
  
  const city = useMemo(() => {
    if (authentications.length === 0) return null;
    
    // Create a map to combine city data by city name
    const cityMap = new Map();
    
    authentications.forEach(auth => {
      const cityData = auth?.audience_information?.detailed_audience_stats?.city;
      if (!cityData) return;
      
      cityData.forEach(item => {
        if (!item || !item.name) return;
        
        // Skip entries without lat/lng coordinates
        if (item.lat == null || item.lng == null) return;
        
        const cityKey = `${item.name}-${item.country_code || ''}`;
        const existing = cityMap.get(cityKey);
        if (existing) {
          existing.count++;
          existing.percentage += item.percentage || 0;
        } else {
          cityMap.set(cityKey, {
            name: item.name,
            country_code: item.country_code,
            lat: item.lat,
            lng: item.lng,
            percentage: item.percentage || 0,
            count: 1
          });
        }
      });
    });
    
    // Calculate average percentages and convert to array
    return Array.from(cityMap.values())
      .map(item => ({
        name: item.name,
        country_code: item.country_code,
        lat: item.lat,
        lng: item.lng,
        percentage: item.percentage / item.count
      }))
      .sort((a, b) => b.percentage - a.percentage);
  }, [authentications]);
  
  const countryNames = useMemo(() => country?.map(({ name, country_code }) => {
    return `${getUnicodeFlagIcon(country_code)} ${country_code}`
  }), [country])

  const option = useMemo(() => ({
    xAxis: {
      data: countryNames && [...countryNames] || [],
      axisLabel: {
        interval: 0,
        align: 'center',
        padding: [0, 0, 0, 0],
        margin: 16
      },
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      splitLine: {
        show: false
      },
    },
    yAxis: {
      axisLine: {
        show: false
      },
      axisTick: {
        show: false
      },
      splitLine: {
        show: false
      }
    },
    series: [
      {
        type: 'bar',
        data: country?.map(({ percentage }) => ({
          value: +(percentage * 100).toFixed(2),
          label: { show: true, position: 'top', formatter: '{c}%' }
        })),
        itemStyle: {
          barBorderRadius: [10, 10, 0, 0],
          borderWidth: 1,
          borderType: 'solid',
          borderColor: '#2CCDB4',
          color: '#2CCDB4'
        },
        label: {
          fontSize: 12,
          fontWeight: 'bold'
        }
      }
    ],
    grid: {
      left: 20,
      right: 20,
      top: 40,
      bottom: 50,
      containLabel: true
    },
    textStyle: {
      fontSize: 14
    }
  }), [countryNames, country])

  return (
    <div className="flex flex-col h-full">
      <div className="mb-4 flex items-center p-4">
        <div className="text-gray-400 mr-2"><Flag /></div>
        <p className={`font-bold text-xl dark:text-white ${advSideScenario || values.audience_countries_visible ? '' : 'opacity-30'}`}>Audience Countries</p>
        {!advSideScenario && <Switch name="audience_countries_visible" value={values.audience_countries_visible} onChange={({ target: { checked }}) => setFieldValue('audience_countries_visible', checked, true)} eyeSwitch />}
      </div>
      <div className={`${advSideScenario || values.audience_countries_visible ? '' : 'opacity-30'}`}>
        <MapContainer center={[47, 8]} zoom={5} scrollWheelZoom={false} style={{ height: 500, width: '100%', zIndex: 0 }}>
          <TileLayer
            attribution='Tiles &copy; Esri &mdash; Esri, DeLorme, NAVTEQ'
            url="https://server.arcgisonline.com/ArcGIS/rest/services/Canvas/World_Light_Gray_Base/MapServer/tile/{z}/{y}/{x}"
          />
          {country?.filter(item => item.polygon != null).map(({ name, polygon, percentage }) => (
            <GeoJSON key={name} data={polygon} style={{ fillColor: '#2CCDB4', color: '#2CCDB4' }} >
              <Popup>{name}: {(percentage * 100).toFixed(2)}%</Popup>
            </GeoJSON>
          ))}
          {city?.filter(item => item.lat != null && item.lng != null).map(({ name, lng, lat, percentage }) => (
            <Circle
              key={name}
              center={[lat, lng]}
              pathOptions={{
                color: '#183e59',
                fillColor: '#66afe9',
                fillOpacity: 0.5
              }}
              radius={300000 * percentage} >
              <Popup>{name}: {(percentage * 100).toFixed(2)}%</Popup>
            </Circle>
          ))}
        </MapContainer>
        <div className="flex-grow flex items-center justify-center">
          <EChartsReact option={option} style={{ width: '100%', height: '350px' }} />
        </div>
      </div>
    </div>
  )
})
